import type { ReaderPreferences, ReaderState, ReadingHistoryItem } from "./types";

export const READER_STORAGE_KEY = "xhalo-poetry-reader-v2";
export const DEFAULT_PREFERENCES: ReaderPreferences = {
  theme: "system", fontSize: 30, lineHeight: 1.9, contentWidth: 720, alignment: "auto", languageMode: "bilingual",
};
export const EMPTY_READER_STATE: ReaderState = { schemaVersion: 2, preferences: DEFAULT_PREFERENCES, favorites: [], history: [], updatedAt: "1970-01-01T00:00:00.000Z" };

const clamp = (value: unknown, min: number, max: number, fallback: number) =>
  typeof value === "number" && Number.isFinite(value) ? Math.min(max, Math.max(min, value)) : fallback;

export function migrateReaderState(value: unknown): ReaderState {
  if (!value || typeof value !== "object") return structuredClone(EMPTY_READER_STATE);
  const candidate = value as Partial<ReaderState> & { preferences?: Partial<ReaderPreferences> };
  const theme = candidate.preferences?.theme;
  const alignment = candidate.preferences?.alignment;
  const languageMode = candidate.preferences?.languageMode;
  return {
    schemaVersion: 2,
    preferences: {
      theme: theme === "system" || theme === "light" || theme === "sepia" || theme === "dark" ? theme : DEFAULT_PREFERENCES.theme,
      fontSize: clamp(candidate.preferences?.fontSize, 18, 44, DEFAULT_PREFERENCES.fontSize),
      lineHeight: clamp(candidate.preferences?.lineHeight, 1.35, 2.4, DEFAULT_PREFERENCES.lineHeight),
      contentWidth: clamp(candidate.preferences?.contentWidth, 520, 960, DEFAULT_PREFERENCES.contentWidth),
      alignment: alignment === "auto" || alignment === "left" || alignment === "center" ? alignment : DEFAULT_PREFERENCES.alignment,
      languageMode: languageMode === "original" || languageMode === "translation" || languageMode === "bilingual" ? languageMode : DEFAULT_PREFERENCES.languageMode,
    },
    favorites: Array.isArray(candidate.favorites) ? candidate.favorites.filter((item): item is string => typeof item === "string").slice(-1000) : [],
    history: Array.isArray(candidate.history) ? candidate.history.filter(isHistory).slice(-100) : [],
    updatedAt: typeof candidate.updatedAt === "string" ? candidate.updatedAt : "1970-01-01T00:00:00.000Z",
  };
}

function isHistory(value: unknown): value is ReadingHistoryItem {
  if (!value || typeof value !== "object") return false;
  const item = value as Partial<ReadingHistoryItem>;
  return typeof item.workId === "string" && typeof item.editionId === "string" && typeof item.title === "string" &&
    typeof item.creator === "string" && typeof item.language === "string" && typeof item.href === "string" &&
    typeof item.progress === "number" && typeof item.updatedAt === "string";
}

export function loadReaderState(storage: Pick<Storage, "getItem"> = window.localStorage): ReaderState {
  try {
    const value = storage.getItem(READER_STORAGE_KEY);
    return value ? migrateReaderState(JSON.parse(value)) : structuredClone(EMPTY_READER_STATE);
  } catch { return structuredClone(EMPTY_READER_STATE); }
}

export function saveReaderState(state: ReaderState, storage: Pick<Storage, "setItem"> = window.localStorage) {
  storage.setItem(READER_STORAGE_KEY, JSON.stringify({ ...state, favorites: state.favorites.slice(-1000), history: state.history.slice(-100) }));
}
